import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';

const StyledTruncatedText = styled.div<{ $lines: number }>`
  // limit to a specified number of lines of text, ending in ellipsis
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => props.$lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export type TruncatedTextProps = {
  children: string | null;
  lines?: number;
  className?: string;
  /** Where the tooltip should appear relative to the text. Default is 'auto'. */
  tooltipPlacement?: 'auto' | 'top' | 'right' | 'bottom' | 'left';
};

/**
 * Displays a string of text, limited to a specified number of lines, ending in ellipsis, with a tooltip containing the full text.
 *
 * The tooltip is only shown if the text is truncated. We handle the truncation styling with standard CSS but use JS to determine if the tooltip is needed.
 */
export const TruncatedText = ({ children, lines = 3, className, tooltipPlacement = 'auto' }: TruncatedTextProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const checkTruncation = useCallback(() => {
    const textElement = textRef.current;
    if (textElement) {
      const { scrollHeight, clientHeight, scrollWidth, clientWidth } = textElement;
      // hack: sometimes the scrollHeight is 1px taller than the clientHeight even if the text is not truncated
      // (possibly due to sub-pixel rendering or rounding issues in how browsers calculate element dimensions),
      // so we subtract 1 pixel just to be safe
      const normalizedScrollHeight = scrollHeight - 1;

      setIsTruncated(normalizedScrollHeight > clientHeight || scrollWidth > clientWidth);
    }
  }, []);

  useLayoutEffect(() => {
    checkTruncation();
  }, [children, lines, checkTruncation]);

  useEffect(() => {
    window.addEventListener('resize', checkTruncation);
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [checkTruncation]);

  if (!children) {
    return null;
  }

  const content = (
    <StyledTruncatedText ref={textRef} $lines={lines} className={className}>
      {children}
    </StyledTruncatedText>
  );

  return isTruncated ? (
    <Tooltip content={children} placement={tooltipPlacement}>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default TruncatedText;
